import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Check, Landmark, Mail, FileText } from "lucide-react";
import { FadeUp } from "@/components/motion-ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CopyField({ label, value, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copié`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Impossible de copier");
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={testId}
      className="group w-full flex items-center justify-between gap-3 bg-white rounded-2xl p-4 border border-pink-100 hover:border-pink-300 hover:shadow-md transition-all text-left"
    >
      <div className="min-w-0">
        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-neutral-500">
          {label}
        </p>
        <p className="font-display text-lg md:text-xl font-black text-neutral-900 truncate mt-0.5">
          {value || "—"}
        </p>
      </div>
      <span className="h-10 w-10 shrink-0 rounded-full bg-pink-50 group-hover:bg-pink-600 flex items-center justify-center transition-colors">
        {copied ? (
          <Check size={16} className="text-pink-600 group-hover:text-white" />
        ) : (
          <Copy size={14} className="text-pink-600 group-hover:text-white" />
        )}
      </span>
    </button>
  );
}

export default function PaymentInfo() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/payment-info`)
      .then((res) => mounted && setInfo(res.data))
      .catch(() => mounted && setInfo({ configured: false, fallback_message: "Les coordonnées bancaires seront communiquées par email." }))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section
      id="paiement"
      data-testid="payment-section"
      className="relative px-5 md:px-8 py-24 md:py-32 bg-neutral-950 text-white overflow-hidden"
    >
      <div className="absolute top-20 -right-40 w-[520px] h-[520px] blob-pink pointer-events-none" />
      <div className="absolute -bottom-20 -left-40 w-[420px] h-[420px] blob-cyan pointer-events-none opacity-60" />

      <div className="relative max-w-6xl mx-auto">
        <FadeUp>
          <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/15 rounded-full px-4 py-2 text-xs font-black uppercase tracking-[0.25em] text-pink-300">
            <Landmark size={14} /> Paiement
          </span>
          <h2 className="font-display mt-5 text-4xl md:text-6xl lg:text-7xl font-black uppercase tracking-tight leading-[0.95]">
            Paiement par <br />
            <span className="text-pink-500">virement</span> bancaire.
          </h2>
          <p className="mt-6 max-w-2xl text-lg text-white/70 font-medium leading-relaxed">
            Après ta réservation (SimplyBook) ou ta précommande, tu reçois les
            coordonnées bancaires avec une référence unique. Simple, sans
            commission, direct sur le compte LaGigaFam.
          </p>
        </FadeUp>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <FadeUp className="lg:col-span-7" delay={0.1}>
            <div className="bg-white text-neutral-900 rounded-[2rem] p-6 md:p-8 shadow-[0_30px_80px_-20px_rgba(255,0,127,0.4)]">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-2xl bg-pink-600 flex items-center justify-center">
                  <Landmark size={20} className="text-white" />
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.25em] text-pink-600">
                    Coordonnées bancaires
                  </p>
                  <p className="font-display text-xl font-black">
                    LAGIGAFAM — Virement SEPA
                  </p>
                </div>
              </div>

              {loading ? (
                <div className="space-y-3">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="h-16 bg-pink-50 rounded-2xl animate-pulse" />
                  ))}
                </div>
              ) : info?.configured ? (
                <div className="space-y-3">
                  <CopyField
                    label="Bénéficiaire"
                    value={info.account_holder}
                    testId="pay-copy-holder"
                  />
                  <CopyField label="IBAN" value={info.iban} testId="pay-copy-iban" />
                  <CopyField label="BIC" value={info.bic} testId="pay-copy-bic" />
                  <CopyField
                    label="Banque"
                    value={info.bank_name}
                    testId="pay-copy-bank"
                  />
                </div>
              ) : (
                <div className="bg-pink-50 border border-pink-200 rounded-2xl p-5 flex items-start gap-3">
                  <Mail size={20} className="text-pink-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-display text-base font-black text-neutral-900">
                      Coordonnées envoyées par email
                    </p>
                    <p className="text-sm text-neutral-700 mt-1 font-medium">
                      {info?.fallback_message ||
                        "Dès validation de ta réservation ou précommande, nous t'envoyons l'IBAN et la référence par email."}
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-6 flex items-start gap-3 text-xs text-neutral-600 font-medium">
                <FileText size={14} className="shrink-0 mt-0.5 text-pink-600" />
                <p>
                  Pense à mentionner <strong>ton nom + ta date de rendez-vous</strong>{" "}
                  en libellé du virement pour accélérer la confirmation.
                </p>
              </div>
            </div>
          </FadeUp>

          <FadeUp className="lg:col-span-5" delay={0.2}>
            <div className="h-full bg-gradient-to-br from-pink-600 to-fuchsia-600 rounded-[2rem] p-8 flex flex-col justify-between min-h-[320px]">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-white/80">
                  3 étapes • Zéro stress
                </p>
                <h3 className="font-display mt-3 text-2xl md:text-3xl font-black uppercase leading-tight">
                  Comment payer ?
                </h3>
              </div>
              <ol className="space-y-4 mt-6">
                {[
                  "Réserve ta séance (SimplyBook) ou précommande tes élastiques.",
                  "Reçois les coordonnées bancaires + ta référence unique par email.",
                  "Effectue le virement en mentionnant ton nom en libellé. Confirmation sous 24h.",
                ].map((step, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="h-7 w-7 rounded-full bg-white text-pink-600 flex items-center justify-center font-display font-black text-sm shrink-0">
                      {i + 1}
                    </span>
                    <p className="text-sm text-white/95 font-medium leading-relaxed">
                      {step}
                    </p>
                  </li>
                ))}
              </ol>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
